import { FileText, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeader, ConfidenceBar, StatusBadge, DetailLabel, PropRow, CriterionCheck, UseCaseFlow, PersonaCard, stripMarkdown } from './shared'
import type { TreeProps } from './shared'

export function PrdTree({ tree, search, expandedSections, toggleSection, expandedDetail, setExpandedDetail }: TreeProps) {
  // Scope Reference defaults to collapsed; Functional Requirements defaults to open
  const isScopeOpen = expandedSections.has('prd-0')
  const isFROpen = expandedSections.has('prd-1') || expandedSections.size === 0

  return (
    <div>
      <div className="grid grid-cols-[70px_80px_1fr_60px_80px_100px] px-4 py-1.5 bg-warm-100 border-b border-border text-[10px] font-semibold text-text-muted uppercase tracking-wider">
        <div>Type</div>
        <div>Sub-type</div>
        <div>Requirement</div>
        <div>RICE</div>
        <div>Evidence</div>
        <div>Status</div>
      </div>

      <SectionHeader num={1} title="Scope Reference" count={2} isOpen={isScopeOpen} onToggle={() => toggleSection('prd-0')} />
      {isScopeOpen && (
        <>
          <div className="px-4 py-2.5 pl-10 border-b border-border-subtle">
            <div className="text-[13.5px] font-medium">{tree.vision.name}</div>
            {tree.vision.visionStatement && (
              <p className="text-[12px] text-text-muted mt-1 line-clamp-2">{stripMarkdown(tree.vision.visionStatement)}</p>
            )}
          </div>
          <div className="px-4 py-2.5 pl-10 border-b border-border-subtle">
            <div className="text-[12px] text-text-muted">Capabilities</div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {tree.capabilities.map(c => (
                <span key={c.capability.id} className="text-[12px] font-medium px-2 py-0.5 rounded bg-warm-100 text-text-secondary border border-border-subtle">
                  {c.capability.name}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      <SectionHeader num={2} title="Functional Requirements" count={tree.capabilities.reduce((s, c) => s + c.features.length, 0)} isOpen={isFROpen} onToggle={() => toggleSection('prd-1')} />
      {isFROpen && tree.capabilities.map(({ capability, features }) => (
        <div key={capability.id}>
          <div className="flex items-center justify-between px-4 py-2 pl-8 bg-warm-50/50 border-b border-border-subtle">
            <span className="font-semibold text-[13px]">Epic: {capability.name}</span>
            <span className="text-xs text-text-muted">{features.length} FRs</span>
          </div>

          {features.filter(f => !search || f.name.toLowerCase().includes(search.toLowerCase())).map((f) => {
            const links = f.linkedPains.length + f.linkedGains.length + f.linkedJtbds.length
            const isExpanded = expandedDetail === f.id
            const meta = f.metadata || {}
            const subType = meta.subType || 'User Story'
            return (
              <div key={f.id}>
                <div
                  className={cn('grid grid-cols-[70px_80px_1fr_60px_80px_100px] px-4 py-2.5 pl-10 border-b border-border-subtle items-center cursor-pointer hover:bg-warm-50 transition-colors', isExpanded && 'bg-warm-50')}
                  onClick={() => setExpandedDetail(isExpanded ? null : f.id)}
                >
                  <div><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-500/10 text-green-700">Functional</span></div>
                  <div className="text-[11.5px] text-text-muted">{subType}</div>
                  <div>
                    <div className="text-[13.5px] font-medium">{f.name}</div>
                    <div className="text-[12px] text-text-muted truncate">{f.prdDescription || f.visionDescription || ''}</div>
                  </div>
                  <div>{f.riceScore ? <span className="text-[11px] font-bold text-solution bg-solution/10 px-1.5 py-0.5 rounded">{Math.round(f.riceScore)}</span> : null}</div>
                  <div>{f.evidenceConfidence ? <ConfidenceBar value={f.evidenceConfidence} links={links} /> : null}</div>
                  <div><StatusBadge status={f.status} /></div>
                </div>

                {isExpanded && (
                  <div className="px-6 py-5 pl-12 bg-warm-50 border-b border-border-subtle animate-in fade-in slide-in-from-top-1 duration-150">
                    <div className="grid grid-cols-[1fr_220px] gap-6">
                      {/* Left column: content */}
                      <div>
                        <DetailLabel icon={<FileText className="w-3.5 h-3.5" />}>Description</DetailLabel>
                        <p className="text-[13px] text-text-secondary leading-relaxed mb-4">{f.prdDescription || f.visionDescription || 'No description'}</p>

                        {meta.userStory && (
                          <>
                            <DetailLabel>User Story</DetailLabel>
                            <p className="text-[13.5px] italic text-text-secondary leading-relaxed mb-4">
                              &ldquo;{meta.userStory}&rdquo;
                            </p>
                          </>
                        )}

                        {meta.useCaseFlow && meta.useCaseFlow.length > 0 && (
                          <>
                            <DetailLabel>Use Case Flow</DetailLabel>
                            <div className="mb-4">
                              <UseCaseFlow steps={meta.useCaseFlow} />
                            </div>
                          </>
                        )}

                        {f.acceptanceCriteria.length > 0 && (
                          <>
                            <DetailLabel>Acceptance Criteria</DetailLabel>
                            <div className="flex flex-col gap-1.5 mb-4">
                              {f.acceptanceCriteria.map((ac, i) => (
                                <div key={i} className="flex items-center gap-2 text-[12.5px] text-text-secondary">
                                  <CriterionCheck met={ac.status === 'met'} />
                                  {ac.text}
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        {(f.linkedPains.length > 0 || f.linkedGains.length > 0) && (
                          <>
                            <DetailLabel icon={<Users className="w-3.5 h-3.5" />}>Linked Evidence</DetailLabel>
                            <div className="flex flex-col gap-1">
                              {f.linkedPains.map((p, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-[12px]">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                                  <span className="text-text-muted">Pain:</span> {p.label}
                                </div>
                              ))}
                              {f.linkedGains.map((g, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-[12px]">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                  <span className="text-text-muted">Gain:</span> {g.label}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>

                      {/* Right column: properties + persona */}
                      <div>
                        <div className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Properties</div>
                        <PropRow label="Owner" value={meta.owner || '—'} />
                        <PropRow label="Sprint" value={meta.sprint || '—'} />
                        <PropRow label="Effort" value={meta.effort || '—'} />
                        <PropRow label="Due Date" value={meta.dueDate ? new Date(meta.dueDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'} />

                        {meta.linkedPersona && (
                          <PersonaCard
                            name={meta.linkedPersona.name}
                            role={meta.linkedPersona.role}
                            primaryNeed={meta.linkedPersona.primaryNeed}
                            keyPain={meta.linkedPersona.keyPain}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

